import React, { useEffect, useRef, useState } from "react";
import emailjs from "emailjs-com";

const RATE_LIMIT_STORAGE_KEY = "contact_form_submissions_v1";
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const RATE_LIMIT_MAX_SUBMISSIONS = 3;
const MIN_TIME_ON_PAGE_MS = 1500;
const MESSAGE_MAX_CHARS = 5000;
const SUBJECT_MAX_CHARS = 160;
const NAME_MAX_CHARS = 120;
const PHONE_MAX_CHARS = 50;
const LINKEDIN_URL = "https://www.linkedin.com/in/temefford";

// No email address is hardcoded here by design. Set
// REACT_APP_CONTACT_FALLBACK_EMAIL to publish one; otherwise we direct people
// to LinkedIn so no address is exposed in the client bundle.
const FallbackContact = ({ fallbackEmail }) =>
    fallbackEmail ? (
        <a href={`mailto:${fallbackEmail}`}>{fallbackEmail}</a>
    ) : (
        <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">
            LinkedIn
        </a>
    );

const Result = ({ status, fallbackEmail }) => {
    if (status === "success") {
        return (
            <p className="success-message">
                Your message has been successfully sent. I will contact you soon.
            </p>
        );
    }

    if (status === "disabled") {
        return (
            <p className="error-message">
                Contact form is temporarily unavailable. Please reach me via{" "}
                <FallbackContact fallbackEmail={fallbackEmail} />.
            </p>
        );
    }

    if (status === "rate_limited") {
        return (
            <p className="error-message">
                Too many messages in a short time. Please wait a bit and try again.
            </p>
        );
    }

    if (status === "error") {
        return (
            <p className="error-message">
                Something went wrong sending your message. Please try again in a moment.
            </p>
        );
    }

    return null;
};

function ContactForm() {
    const emailJsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const emailJsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const emailJsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const fallbackEmail = process.env.REACT_APP_CONTACT_FALLBACK_EMAIL || "";

    const [status, setStatus] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const mountedAtMsRef = useRef(Date.now());
    const isConfigured = Boolean(
        emailJsServiceId && emailJsTemplateId && emailJsPublicKey
    );

    useEffect(() => {
        if (!status) return;

        const timer = window.setTimeout(() => {
            setStatus(null);
        }, 5000);

        return () => {
            window.clearTimeout(timer);
        };
    }, [status]);

    const recordSubmissionAttempt = () => {
        const now = Date.now();
        try {
            const raw = window.localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
            const existing = raw ? JSON.parse(raw) : [];
            const normalized = Array.isArray(existing) ? existing : [];
            const recent = normalized
                .filter((ts) => typeof ts === "number" && now - ts < RATE_LIMIT_WINDOW_MS)
                .concat(now);
            window.localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(recent));
        } catch {
            // If localStorage is unavailable, fail open (we still have honeypot + EmailJS protections).
        }
    };

    const isRateLimited = () => {
        const now = Date.now();
        try {
            const raw = window.localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
            const existing = raw ? JSON.parse(raw) : [];
            const normalized = Array.isArray(existing) ? existing : [];
            const recent = normalized.filter(
                (ts) => typeof ts === "number" && now - ts < RATE_LIMIT_WINDOW_MS
            );
            return recent.length >= RATE_LIMIT_MAX_SUBMISSIONS;
        } catch {
            return false;
        }
    };

    const sendEmail = (e) => {
        e.preventDefault();
        if (!isConfigured) {
            setStatus("disabled");
            return;
        }
        if (isSending) return;

        const form = e.target;

        const timeOnPageMs = Date.now() - mountedAtMsRef.current;
        if (timeOnPageMs < MIN_TIME_ON_PAGE_MS) {
            // Bot-like behavior: submitting immediately after page load.
            setStatus("error");
            return;
        }

        const honeypotValue = form?.elements?.company?.value;
        if (typeof honeypotValue === "string" && honeypotValue.trim().length > 0) {
            // Honeypot triggered. Pretend success to avoid signaling.
            setStatus("success");
            form.reset();
            return;
        }

        if (isRateLimited()) {
            setStatus("rate_limited");
            return;
        }

        setIsSending(true);
        emailjs
            .sendForm(emailJsServiceId, emailJsTemplateId, form, emailJsPublicKey)
            .then(() => {
                setStatus("success");
                form.reset();
            })
            .catch(() => {
                setStatus("error");
            })
            .finally(() => {
                recordSubmissionAttempt();
                setIsSending(false);
            });
    };

    return (
        <form action="" onSubmit={sendEmail}>
            {/* Honeypot field for basic bot filtering */}
            <div
                className="rn-form-group"
                style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
                aria-hidden="true"
            >
                <label htmlFor="company">Company</label>
                <input
                    id="company"
                    type="text"
                    name="company"
                    autoComplete="off"
                    tabIndex={-1}
                />
            </div>

            <div className="rn-form-group">
                <input 
                type="text"
                name="fullname"
                placeholder="Your Name"
                autoComplete="name"
                maxLength={NAME_MAX_CHARS}
                required
                />
            </div>

            <div className="rn-form-group">
                <input 
                type="email"
                name="email"
                placeholder="Your Email"
                autoComplete="email"
                required
                />
            </div>

            <div className="rn-form-group">
                <input 
                type="text"
                name="phone"
                placeholder="Phone Number"
                autoComplete="tel"
                maxLength={PHONE_MAX_CHARS}
                />
            </div>

            <div className="rn-form-group">
                <input 
                type="text"
                name="subject"
                placeholder="Subject"
                maxLength={SUBJECT_MAX_CHARS}
                required
                />
            </div>

            <div className="rn-form-group">
                <textarea 
                name="message"
                placeholder="Your Message"
                rows={6}
                maxLength={MESSAGE_MAX_CHARS}
                required
                >
                </textarea>
            </div>

            <div className="rn-form-group">
                <button
                    className="rn-button-style--2 btn-solid"
                    type="submit"
                    value="submit"
                    name="submit"
                    id="mc-embedded-subscribe"
                    disabled={!isConfigured || isSending}
                >
                    {/* With no fallback email published, the actionable link is the
                        LinkedIn one in the message below — don't promise an email. */}
                    {!isConfigured
                        ? (fallbackEmail ? "Email me instead" : "Form unavailable")
                        : (isSending ? "Sending..." : "Submit Now")}
                </button>
            </div> 

            <div className="rn-form-group" aria-live="polite">
                {!isConfigured ? (
                    <p className="error-message">
                        Contact form is currently disabled. Please reach me via{" "}
                        <FallbackContact fallbackEmail={fallbackEmail} />.
                    </p>
                ) : (
                    <Result status={status} fallbackEmail={fallbackEmail} />
                )}
            </div> 
        </form>
    )
}
export default ContactForm;
